// /pages/HR/components/kpis/KPICard.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './KPICard.css';

/**
 * Modern KPI Card Component
 * Clean, minimal design with smooth animations
 */
const KPICard = React.memo(({ 
  value = 0,
  label = '',
  sublabel = '',
  icon = null,
  trend = null,
  trendLabel = '',
  format = 'number', // 'number', 'percentage', 'currency', 'decimal'
  decimals = 0,
  loading = false,
  onClick = null,
  className = '',
  variant = 'default', // 'default', 'success', 'danger', 'warning'
  animate = true,
  size = 'medium', // 'small', 'medium', 'large'
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const animationRef = useRef(null);

  // Animate number counting effect
  useEffect(() => {
    if (animate && !loading && typeof value === 'number') {
      const duration = 1200;
      const steps = 30;
      let step = 0;

      const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

      animationRef.current = setInterval(() => {
        step++;
        const progress = easeOutQuart(step / steps);
        const currentValue = value * progress;
        
        if (step >= steps) {
          setDisplayValue(value);
          clearInterval(animationRef.current);
        } else {
          setDisplayValue(currentValue);
        }
      }, duration / steps);

      return () => {
        if (animationRef.current) {
          clearInterval(animationRef.current);
        }
      };
    } else {
      setDisplayValue(value);
    }
  }, [value, animate, loading]);

  // Format display value
  const formatValue = useCallback((val) => {
    if (typeof val !== 'number') return val;
    
    switch (format) {
      case 'percentage':
        return `${val.toFixed(decimals)}%`;
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }).format(val);
      case 'decimal':
        return val.toFixed(decimals);
      case 'compact':
        return new Intl.NumberFormat('en-US', {
          notation: 'compact',
          compactDisplay: 'short',
          maximumFractionDigits: 1
        }).format(val);
      case 'number':
      default:
        return new Intl.NumberFormat('en-US').format(Math.round(val));
    }
  }, [format, decimals]);

  // Get size classes
  const sizeClasses = useMemo(() => {
    return {
      small: 'kpi-card-small',
      medium: 'kpi-card-medium',
      large: 'kpi-card-large'
    }[size] || 'kpi-card-medium';
  }, [size]);

  // Get variant classes
  const variantClasses = useMemo(() => {
    return {
      default: '',
      success: 'kpi-card-success',
      danger: 'kpi-card-danger',
      warning: 'kpi-card-warning'
    }[variant] || '';
  }, [variant]);

  // Format trend display
  const trendDisplay = useMemo(() => {
    if (trend === null || trend === undefined) return null;
    
    const isPositive = trend >= 0;
    const trendValue = Math.abs(trend);
    const arrow = isPositive ? '↑' : '↓';
    
    return {
      isPositive,
      value: trendValue,
      arrow,
      text: trendLabel || `${isPositive ? '+' : '-'}${trendValue}%`
    };
  }, [trend, trendLabel]);

  // Handlers
  const handleClick = useCallback(() => {
    if (onClick) onClick();
  }, [onClick]);

  const handleKeyPress = useCallback((e) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleClick();
    }
  }, [onClick, handleClick]);

  const formattedValue = useMemo(() => 
    formatValue(displayValue),
    [formatValue, displayValue]
  );

  return (
    <div 
      className={`
        kpi-card 
        ${sizeClasses} 
        ${variantClasses} 
        ${className} 
        ${onClick ? 'kpi-card-clickable' : ''} 
        ${loading ? 'kpi-card-loading' : ''} 
        ${isHovered ? 'kpi-card-hovered' : ''}
      `}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role={onClick ? 'button' : 'article'}
      tabIndex={onClick ? 0 : -1}
      onKeyPress={handleKeyPress}
    >
      {/* Main Content */}
      <div className="kpi-card-content">
        {/* Header with icon and label */}
        <div className="kpi-card-header">
          {icon && (
            <div className="kpi-card-icon">
              {typeof icon === 'string' ? (
                <span className="kpi-icon-text">{icon}</span>
              ) : (
                icon
              )}
            </div>
          )}
          <div className="kpi-card-labels">
            <div className="kpi-card-label">{label}</div>
          </div>
        </div>

        {/* Value Section */}
        <div className="kpi-card-body">
          {loading ? (
            <div className="kpi-skeleton">
              <div className="skeleton-value"></div>
              <div className="skeleton-trend"></div>
            </div>
          ) : (
            <>
              <div className="kpi-card-value">
                {formattedValue}
              </div>
              
              {/* Trend Indicator */}
              {trendDisplay && (
                <div className={`kpi-card-trend ${trendDisplay.isPositive ? 'trend-positive' : 'trend-negative'}`}>
                  <span className="trend-arrow">{trendDisplay.arrow}</span>
                  <span className="trend-text">{trendDisplay.text}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Hover Effect Overlay */}
      {onClick && (
        <div className="kpi-card-hover-effect"></div>
      )}
    </div>
  );
});

KPICard.displayName = 'KPICard';

export default KPICard;